import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export enum ConversionDirection {
  CSV_TO_JSON = "CSV_TO_JSON",
  JSON_TO_CSV = "JSON_TO_CSV"
}

export interface CsvJsonConverterState {
  inputData: string;
  outputData: string;
  conversionDirection: ConversionDirection;
  csvDelimiter: string;
  jsonIndentation: number;
  includeHeaders: boolean;
  error: string | null;
  isValidInput: boolean;
}

const initialState: CsvJsonConverterState = {
  inputData: "",
  outputData: "",
  conversionDirection: ConversionDirection.CSV_TO_JSON,
  csvDelimiter: ",",
  jsonIndentation: 2,
  includeHeaders: true,
  error: null,
  isValidInput: false,
};

export const CsvJsonConverterSlice = createSlice({
  name: "csvJsonConverter",
  initialState,
  reducers: {
    inputDataChanged: (state, action: PayloadAction<string>) => {
      state.inputData = action.payload;
      state.error = null;
      state.isValidInput = action.payload.trim().length > 0;
      
      // Auto-convert when input changes
      if (state.isValidInput) {
        try {
          if (state.conversionDirection === ConversionDirection.CSV_TO_JSON) {
            state.outputData = convertCsvToJson(
              action.payload,
              state.csvDelimiter,
              state.includeHeaders,
              state.jsonIndentation
            );
          } else {
            state.outputData = convertJsonToCsv(
              action.payload,
              state.csvDelimiter,
              state.includeHeaders
            );
          }
        } catch (error) {
          state.error = error instanceof Error ? error.message : "Conversion failed";
          state.outputData = "";
          state.isValidInput = false;
        }
      } else {
        state.outputData = "";
      }
    },
    conversionDirectionChanged: (state, action: PayloadAction<ConversionDirection>) => {
      state.conversionDirection = action.payload;
      state.error = null;
      
      // Swap input and output when direction changes
      const tempInput = state.inputData;
      state.inputData = state.outputData;
      state.outputData = tempInput;
      
      // Re-validate and convert
      if (state.inputData.trim().length > 0) {
        try {
          if (state.conversionDirection === ConversionDirection.CSV_TO_JSON) {
            state.outputData = convertCsvToJson(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders,
              state.jsonIndentation
            );
          } else {
            state.outputData = convertJsonToCsv(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders
            );
          }
          state.isValidInput = true;
        } catch (error) {
          state.error = error instanceof Error ? error.message : "Conversion failed";
          state.outputData = "";
          state.isValidInput = false;
        }
      }
    },
    csvDelimiterChanged: (state, action: PayloadAction<string>) => {
      state.csvDelimiter = action.payload;
      state.error = null;
      
      // Re-convert if we have valid input
      if (state.isValidInput && state.inputData.trim().length > 0) {
        try {
          if (state.conversionDirection === ConversionDirection.CSV_TO_JSON) {
            state.outputData = convertCsvToJson(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders,
              state.jsonIndentation
            );
          } else {
            state.outputData = convertJsonToCsv(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders
            );
          }
        } catch (error) {
          state.error = error instanceof Error ? error.message : "Conversion failed";
          state.outputData = "";
        }
      }
    },
    jsonIndentationChanged: (state, action: PayloadAction<number>) => {
      state.jsonIndentation = action.payload;
      
      // Re-convert if we're converting to JSON and have valid input
      if (state.conversionDirection === ConversionDirection.CSV_TO_JSON && 
          state.isValidInput && state.inputData.trim().length > 0) {
        try {
          state.outputData = convertCsvToJson(
            state.inputData,
            state.csvDelimiter,
            state.includeHeaders,
            state.jsonIndentation
          );
        } catch (error) {
          state.error = error instanceof Error ? error.message : "Conversion failed";
          state.outputData = "";
        }
      }
    },
    includeHeadersChanged: (state, action: PayloadAction<boolean>) => {
      state.includeHeaders = action.payload;
      state.error = null;
      
      // Re-convert if we have valid input
      if (state.isValidInput && state.inputData.trim().length > 0) {
        try {
          if (state.conversionDirection === ConversionDirection.CSV_TO_JSON) {
            state.outputData = convertCsvToJson(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders,
              state.jsonIndentation
            );
          } else {
            state.outputData = convertJsonToCsv(
              state.inputData,
              state.csvDelimiter,
              state.includeHeaders
            );
          }
        } catch (error) {
          state.error = error instanceof Error ? error.message : "Conversion failed";
          state.outputData = "";
        }
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Conversion utility functions
function convertCsvToJson(
  csvData: string,
  delimiter: string,
  includeHeaders: boolean,
  indentation: number
): string {
  if (!csvData.trim()) {
    throw new Error("CSV data is empty");
  }

  const lines = csvData.trim().split('\n');
  if (lines.length === 0) {
    throw new Error("No CSV lines found");
  }

  const headers = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
  const dataLines = includeHeaders ? lines.slice(1) : lines;
  
  if (!includeHeaders && lines.length === 1) {
    // Single line without headers - convert to array of values
    const values = lines[0].split(delimiter).map(v => {
      const rawValue = v.trim();
      const wasQuoted = rawValue.startsWith('"') && rawValue.endsWith('"');
      const trimmed = rawValue.replace(/^"|"$/g, '');
      
      if (wasQuoted) {
        // If it was quoted, keep it as a string regardless of content
        return trimmed;
      } else {
        // Try to parse as number, otherwise keep as string
        const num = Number(trimmed);
        return !isNaN(num) && trimmed !== '' ? num : trimmed;
      }
    });
    return JSON.stringify(values, null, indentation);
  }

  const result = dataLines.map(line => {
    const values = line.split(delimiter);
    const obj: any = {};
    
    headers.forEach((header, index) => {
      const rawValue = values[index] ? values[index].trim() : '';
      const key = includeHeaders ? header : `column_${index + 1}`;
      
      // Check if the value was quoted in CSV (preserve as string)
      const wasQuoted = rawValue.startsWith('"') && rawValue.endsWith('"');
      const value = rawValue.replace(/^"|"$/g, '');
      
      if (wasQuoted) {
        // If it was quoted, keep it as a string regardless of content
        obj[key] = value;
      } else {
        // Try to parse as number, otherwise keep as string
        const num = Number(value);
        obj[key] = !isNaN(num) && value !== '' ? num : value;
      }
    });
    
    return obj;
  });

  return JSON.stringify(result, null, indentation);
}

function convertJsonToCsv(
  jsonData: string,
  delimiter: string,
  includeHeaders: boolean
): string {
  if (!jsonData.trim()) {
    throw new Error("JSON data is empty");
  }

  let parsedData;
  try {
    parsedData = JSON.parse(jsonData);
  } catch (error) {
    throw new Error("Invalid JSON format");
  }

  if (!Array.isArray(parsedData)) {
    throw new Error("JSON must be an array of objects");
  }

  if (parsedData.length === 0) {
    return "";
  }

  // Get all unique keys from all objects
  const allKeys = Array.from(
    new Set(
      parsedData.flatMap(obj => 
        typeof obj === 'object' && obj !== null ? Object.keys(obj) : []
      )
    )
  );

  if (allKeys.length === 0) {
    throw new Error("No valid object properties found in JSON array");
  }

  const csvLines: string[] = [];

  // Add headers if requested
  if (includeHeaders) {
    csvLines.push(allKeys.map(key => `"${key}"`).join(delimiter));
  }

  // Add data rows
  parsedData.forEach(obj => {
    if (typeof obj === 'object' && obj !== null) {
      const row = allKeys.map(key => {
        const value = obj[key];
        if (value === null || value === undefined) {
          return '';
        }
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvLines.push(row.join(delimiter));
    }
  });

  return csvLines.join('\n');
}

export const {
  inputDataChanged,
  conversionDirectionChanged,
  csvDelimiterChanged,
  jsonIndentationChanged,
  includeHeadersChanged,
  clearError,
} = CsvJsonConverterSlice.actions;

export const selectState = (state: RootState) => state.csvJsonConverter;

export default CsvJsonConverterSlice.reducer;
